import EmberRouter from '@embroider/router';

import config from 'webapp/config/environment';

export default class Router extends EmberRouter {
	override location = config.locationType;
	override rootURL = config.rootURL;
}

Router.map(function () {
	// ------ MAIN APP ------

	this.route('genie', { resetNamespace: true }, function () {
		// `chat` is the conversation surface (owns the persistent React mount)
		this.route('chat', { path: '/' }, function () {
			this.route('thread', { path: 'thread/:thread_id' }, function () {
				this.route('artifact', { path: 'artifact/:artifact_id' });
			});
		});
		this.route('settings');
	});

	this.route('artifacts', { path: '/genie/artifacts', resetNamespace: true });

	this.route('routines', { path: '/genie/routines', resetNamespace: true }, function () {
		// Run threads are addressed under their routine, so the detail route owns them.
		this.route('detail', { path: ':routine_id' });
		// A sibling rather than a child of `detail`: the run replaces the detail page, it does not
		// render inside it.
		this.route('run', { path: ':routine_id/run/:run_id' });
	});

	this.route('home', { resetNamespace: true });

	this.route('agents', { resetNamespace: true });

	// The Company metrics page renders in the parent template; its child routes open popups over it.
	this.route('metrics', { resetNamespace: true }, function () {
		this.route('metrics.cleanup', { path: '/clean-up', resetNamespace: true });
		this.route('metrics.edit', { path: '/edit', resetNamespace: true }, function () {
			this.route('metrics.edit.built', { path: '/built/:query_id', resetNamespace: true });
			this.route('metrics.edit.calculated', {
				path: '/calculated/:script_id',
				resetNamespace: true,
			});
			this.route('metrics.edit.horizon', {
				path: '/horizon/:horizon_metric_id',
				resetNamespace: true,
			});
		});
		this.route('metrics.detail', { path: '/detail', resetNamespace: true }); // Redirects to 'metrics.details'. Links in emails and the account app's Data Manager (?metricKey=) lead to it.
		this.route('metrics.details', { path: '/details', resetNamespace: true });
	});

	this.route('metric-library', { resetNamespace: true }, function () {
		this.route(
			'metric-library.source',
			{ path: '/:source_key', resetNamespace: true },
			function () {
				this.route('metric-library.source.specific', {
					path: '/:source_id',
					resetNamespace: true,
				});
			},
		);
	});

	this.route('boards', { path: '/databoards', resetNamespace: true }, function () {
		this.route('tabs');
		this.route('boards.view', { path: '/view', resetNamespace: true }, function () {
			this.route(
				'boards.view.board',
				{
					path: '/:board_id',
					resetNamespace: true,
				},
				function () {
					this.route('limitation');
					this.route('preview');
				},
			);
		});
		this.route('boards.loops', { path: '/loops', resetNamespace: true }, function () {
			this.route('boards.loops.loop', { path: '/:loop_id', resetNamespace: true });
		});
		this.route('boards.account-templates', {
			path: '/account-templates',
			resetNamespace: true,
		});
		this.route(
			'boards.public-templates',
			{ path: '/public-templates', resetNamespace: true },
			function () {
				this.route(
					'boards.public-templates.template',
					{ path: '/:template_id', resetNamespace: true },
					function () {
						this.route('connect');
					},
				);
			},
		);

		// for backwards compatibility - redirects to public-templates
		this.route('boards.templates', { path: '/templates', resetNamespace: true }, function () {
			this.route(
				'boards.templates.template',
				{ path: '/:template_id', resetNamespace: true },
				function () {
					this.route('connect');
				},
			);
		});
	});

	this.route('goals', { resetNamespace: true }, function () {
		this.route('goals.my', { path: '/my', resetNamespace: true });
		this.route('goals.team', { path: '/team', resetNamespace: true });
		this.route('goals.company', { path: '/company', resetNamespace: true });
		this.route('goals.insights', { path: '/insights', resetNamespace: true }, function () {
			this.route('goals.insights.detail', {
				path: '/:goal_id',
				resetNamespace: true,
			});
		});
		this.route('goals.strategy', { path: '/okr-strategy', resetNamespace: true });
	});

	this.route('reports', { resetNamespace: true }, function () {
		this.route('schedules');
		this.route('archive');
		this.route('reports.account-templates', {
			path: '/account-templates',
			resetNamespace: true,
		});
		this.route(
			'reports.public-templates',
			{ path: '/public-templates', resetNamespace: true },
			function () {
				this.route(
					'reports.public-templates.template',
					{ path: '/:template_id', resetNamespace: true },
					function () {
						this.route('connect');
					},
				);
			},
		);
		this.route('reports.view', { path: '/view', resetNamespace: true }, function () {
			this.route('reports.view.report', { path: '/:report_id', resetNamespace: true });
		});
		this.route('reports.editor', { path: '/editor', resetNamespace: true }, function () {
			this.route('reports.editor.report', { path: '/:report_id', resetNamespace: true });
		});
	});

	this.route('designer', { path: '/layout-designer', resetNamespace: true }, function () {
		this.route('designer.board', { path: '/:board_id', resetNamespace: true }, function () {
			this.route('designer.board.block', { path: '/:block_id', resetNamespace: true });
		});
	});

	this.route('datamanager', { path: '/data-manager', resetNamespace: true }, function () {
		this.route(
			'datamanager.connected',
			{ path: '/connected', resetNamespace: true },
			function () {
				this.route('datamanager.connected.datasets.view', {
					path: '/:space_access_id/datasets/view',
					resetNamespace: true,
				});
				this.route('datamanager.connected.datasets.edit', {
					path: '/:space_access_id/datasets/edit',
					resetNamespace: true,
				});
			},
		);
		this.route('datamanager.datasets', { path: '/datasets', resetNamespace: true });
		this.route('datamanager.merged-datasets', {
			path: '/merged-datasets',
			resetNamespace: true,
		});
		this.route('datamanager.connections', { path: '/connections', resetNamespace: true });

		this.route('datamanager.create-new-merge', {
			path: '/create-new-merge',
			resetNamespace: true,
		});
		this.route(
			'datamanager.available',
			{ path: '/available', resetNamespace: true },
			function () {
				this.route('datamanager.available.source-type-info', {
					path: '/:source_key',
					resetNamespace: true,
				});
			},
		);
		this.route(
			'datamanager.auto-connect',
			{ path: '/auto-connect', resetNamespace: true },
			function () {
				this.route('datamanager.auto-connect.source-type', {
					path: '/:source_key',
					resetNamespace: true,
				});
			},
		);
		this.route(
			'datamanager.external-install',
			{ path: '/external-install', resetNamespace: true },
			function () {
				this.route('datamanager.external-install.group', {
					path: '/:group',
					resetNamespace: true,
				});
			},
		);
	});

	this.route('settings', { resetNamespace: true }, function () {
		this.route('settings.users', { path: '/user-management', resetNamespace: true });
		this.route(
			'settings.notifications',
			{ path: '/notifications', resetNamespace: true },
			function () {
				this.route(
					'settings.notifications.type',
					{
						path: '/:type', // using NotificationCreateType enum
						resetNamespace: true,
					},
					function () {
						this.route('settings.notifications.edit', {
							path: '/:id',
							resetNamespace: true,
						});
					},
				);
			},
		);
		this.route('settings.billing', { path: '/billing', resetNamespace: true }, function () {
			this.route('plans');
			this.route('settings.billing.compare', {
				path: '/subscription-changes',
				resetNamespace: true,
			});
			this.route('stripe');
			this.route('callback');
		});
		this.route('settings.level', { path: '/level', resetNamespace: true }, function () {
			this.route('partner');
		});
		this.route('settings.logs', { path: '/activity-log', resetNamespace: true });
	});

	this.route('clients', { resetNamespace: true }, function () {
		this.route('clients.accounts', { path: '/accounts', resetNamespace: true }, function () {
			this.route('new');
		});
		this.route('clients.performance', { path: '/performance', resetNamespace: true });
		this.route('clients.goals', { path: '/goals', resetNamespace: true });
	});

	this.route('teams', { resetNamespace: true }, function () {
		this.route('teams.view', {
			path: '/:team_id',
			resetNamespace: true,
		});
	});

	this.route('wizard', { path: '/databoard-setup', resetNamespace: true }, function () {
		this.route('wizard.source', { path: '/source', resetNamespace: true });
		this.route('wizard.metrics', { path: '/metrics', resetNamespace: true });
		this.route('wizard.connect', { path: '/connect', resetNamespace: true });
		this.route('wizard.starting', { path: '/get-started', resetNamespace: true });
		this.route('wizard.create-metric', { path: '/create-metric', resetNamespace: true });
		this.route('wizard.create-block', { path: '/create-block', resetNamespace: true });
	});

	this.route('onboarding', {
		resetNamespace: true,
	});

	this.route('mobile', { path: '/m', resetNamespace: true }, function () {
		this.route('mobile.home', { path: '/home', resetNamespace: true }, function () {
			this.route('mobile.home.notifications', {
				path: '/notifications',
				resetNamespace: true,
			});
		});
		this.route('mobile.metrics', { path: '/metrics', resetNamespace: true });
		this.route('mobile.goals', { path: '/goals', resetNamespace: true });
		this.route('mobile.board', { path: '/databoards/:board_token', resetNamespace: true });
		this.route('mobile.notifications', { path: '/notifications', resetNamespace: true });
		this.route('mobile.genie', { path: '/genie', resetNamespace: true }, function () {
			this.route('chat', { path: '/' });
			this.route('settings');
			this.route('routines', function () {
				this.route('detail', { path: ':routine_id' });
				this.route('run', { path: ':routine_id/run/:run_id' });
			});
		});
	});

	// index: opens the metric mover (legacy-metrics banner, app-notifications/legacy-metrics.gts);
	// review: the Push API email's "Review metrics" link
	this.route('legacy-metric-migration', { resetNamespace: true }, function () {
		this.route('review');
	});

	this.route('request-second-trial', {
		resetNamespace: true,
	});

	this.route('storybook', { resetNamespace: true }, function () {
		this.route('storybook.bubbles', { path: '/bubbles', resetNamespace: true });
	});

	// ------ PUBLIC DATAWALL URL ------

	this.route('datawall', { path: '/datawall/:token', resetNamespace: true });
	this.route('shared-chat', { path: '/genie/shared/:share_token', resetNamespace: true });
	this.route('shared-artifact', { path: '/shared/artifacts/:share_token', resetNamespace: true });

	// ------ COMMON ------

	this.route('application_loading');

	this.route('not-found', { path: '*path', resetNamespace: true });
});
